// Local-only image override store. Images are read as Data URLs (base64)
// via FileReader and persisted to localStorage so previews survive a page
// reload — no backend, no database, no server upload of any kind.

export interface ImageOverrideMeta {
  dataUrl: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  selectedAt: string;
}

type ImageStoreShape = Record<string, ImageOverrideMeta>;

const IMAGE_STORAGE_KEY = "motorpro_content_admin_images_v1";

export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

function readImageStore(): ImageStoreShape {
  try {
    const raw = localStorage.getItem(IMAGE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeImageStore(store: ImageStoreShape) {
  localStorage.setItem(IMAGE_STORAGE_KEY, JSON.stringify(store));
}

export function getImageOverride(key: string): ImageOverrideMeta | null {
  if (!key) return null;
  const store = readImageStore();
  return store[key] || null;
}

export function setImageOverride(key: string, meta: ImageOverrideMeta) {
  const store = readImageStore();
  store[key] = meta;
  writeImageStore(store);
}

export function removeImageOverride(key: string) {
  const store = readImageStore();
  delete store[key];
  writeImageStore(store);
}

export function clearAllImageOverrides() {
  localStorage.removeItem(IMAGE_STORAGE_KEY);
}

export function listImageOverrides(): ImageStoreShape {
  return readImageStore();
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-HN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
